import PropTypes from 'prop-types';
import { memo, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  ImageList,
  ImageListItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../components/Iconify';
import { fDateTime } from '../../utils/formatTime';
import { fNumber } from '../../utils/formatNumber';
import AdminStatusChip from '../admin/shared/AdminStatusChip';
import OrderEventTimeline from '../@dashboard/order/OrderEventTimeline';
import GiftcardAuditTrailTable from '../@dashboard/order/GiftcardAuditTrailTable';

function DetailRow({ label, value }) {
  return (
    <TableRow>
      <TableCell sx={{ width: 180, borderBottomStyle: 'dashed' }}>
        <Typography variant="subtitle2">{label}</Typography>
      </TableCell>
      <TableCell sx={{ borderBottomStyle: 'dashed' }}>
        <Typography variant="body2" color="text.secondary">
          {value || 'N/A'}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function OwnershipNotice({ details }) {
  if (details?.is_owner) {
    return (
      <Chip
        color="success"
        icon={<Iconify icon="solar:user-check-bold" />}
        label="You own this order"
        sx={{ fontWeight: 700 }}
      />
    );
  }

  if (details?.owner_staff_id) {
    return <Chip color="warning" label={`Owned by ${details.owner_staff_id}`} sx={{ fontWeight: 700 }} />;
  }

  return <Chip color="default" label="Unclaimed order" sx={{ fontWeight: 700 }} />;
}

OwnershipNotice.propTypes = {
  details: PropTypes.object,
};

function OrderImagesPanel({ details }) {
  if (!details?.is_owner) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          borderRadius: 3,
          textAlign: 'center',
          bgcolor: 'grey.100',
          borderStyle: 'dashed',
        }}
      >
        <Iconify icon="solar:eye-closed-bold" width={28} height={28} />
        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5 }}>
          Images are censored
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Claim and own this order before viewing uploaded card images.
        </Typography>
      </Paper>
    );
  }

  if (!details?.images?.length) {
    return (
      <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
        <Typography variant="subtitle2">No images available</Typography>
        <Typography variant="body2" color="text.secondary">
          This order does not have uploaded review images yet.
        </Typography>
      </Paper>
    );
  }

  return (
    <ImageList cols={2} gap={16}>
      {details.images.map((image) => (
        <ImageListItem key={image.id} sx={{ borderRadius: 3, overflow: 'hidden', bgcolor: 'grey.100' }}>
          <img
            src={image.image_url}
            alt={image.tag || 'Order image'}
            loading="lazy"
            style={{ cursor: 'pointer', objectFit: 'cover', height: 220 }}
            onClick={() => window.open(image.image_url, '_blank', 'noopener,noreferrer')}
          />
        </ImageListItem>
      ))}
    </ImageList>
  );
}

OrderImagesPanel.propTypes = {
  details: PropTypes.object,
};

function OrderActionPanel({
  actionLoading,
  canAct,
  canClaim,
  details,
  onClaim,
  onCloseOrder,
  onOpenComplete,
  onOpenDispute,
  onOpenReject,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 3,
        minWidth: { xs: '100%', md: 320 },
        bgcolor: 'background.neutral',
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography variant="subtitle2">Actions</Typography>
          <Button
            variant="text"
            color="inherit"
            onClick={onCloseOrder}
            startIcon={<Iconify icon="solar:close-circle-linear" width={16} height={16} />}
          >
            Close
          </Button>
        </Stack>

        <Divider />

        {canClaim && (
          <>
            <Typography variant="caption" color="text.secondary">
              Claim this order to unlock image review and resolution controls.
            </Typography>
            <LoadingButton
              loading={actionLoading === 'claim'}
              variant="contained"
              onClick={onClaim}
              startIcon={<Iconify icon="solar:user-check-bold" width={16} height={16} />}
              sx={{ alignSelf: 'flex-start' }}
            >
              Claim order
            </LoadingButton>
          </>
        )}

        {canAct && (
          <>
            <Typography variant="caption" color="text.secondary">
              Resolution actions are available because you own this order and backend actions are enabled.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
              <Button
                variant="outlined"
                color="warning"
                onClick={onOpenDispute}
                startIcon={<Iconify icon="solar:danger-triangle-linear" width={16} height={16} />}
              >
                Dispute
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={onOpenReject}
                startIcon={<Iconify icon="solar:close-circle-linear" width={16} height={16} />}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                onClick={onOpenComplete}
                startIcon={<Iconify icon="solar:check-circle-bold" width={16} height={16} />}
              >
                Complete
              </Button>
            </Stack>
          </>
        )}

        {!canClaim && !canAct && (
          <Typography variant="caption" color="text.secondary">
            No resolution actions are currently available for this order state.
          </Typography>
        )}

        {details?.owner_staff_id && !details?.is_owner && (
          <Typography variant="caption" color="warning.main">
            This order is already assigned and can only be resolved by its current owner.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

OrderActionPanel.propTypes = {
  actionLoading: PropTypes.string,
  canAct: PropTypes.bool.isRequired,
  canClaim: PropTypes.bool.isRequired,
  details: PropTypes.object,
  onClaim: PropTypes.func.isRequired,
  onCloseOrder: PropTypes.func.isRequired,
  onOpenComplete: PropTypes.func.isRequired,
  onOpenDispute: PropTypes.func.isRequired,
  onOpenReject: PropTypes.func.isRequired,
};

function CardRedemptionOrderDetailView({
  actionLoading,
  details,
  detailsLoading,
  onClaim,
  onCloseOrder,
  onOpenComplete,
  onOpenDispute,
  onOpenReject,
  order,
}) {
  const [tab, setTab] = useState('details');

  const canClaim = Boolean(order && !details?.is_owner && !details?.owner_staff_id);
  const canAct = Boolean(order && details?.is_owner && details?.allow_actions && order.status === 'pending');
  const imageCount = details?.images?.length || 0;

  const tabContent = useMemo(() => {
    if (!order) {
      return null;
    }

    if (tab === 'images') {
      return <OrderImagesPanel details={details} />;
    }

    if (tab === 'audits') {
      return <GiftcardAuditTrailTable order={order} auditTrails={details?.audits || []} />;
    }

    return (
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <Table>
          <TableBody>
            <DetailRow label="User" value={`${order.first_name || ''} ${order.last_name || ''}`.trim()} />
            <DetailRow label="Order ID" value={order.order_id} />
            <DetailRow label="Category" value={`${order.category} - ${order.type_name || ''} (${order.country || 'N/A'})`} />
            <DetailRow label="Amount" value={`${order.currency} ${fNumber(order.amount)}`} />
            <DetailRow
              label="Settlement amount"
              value={`${order.settlement_currency || 'NGN'} ${fNumber(order.settlement_amount)}`}
            />
            <DetailRow label="Number of cards" value={order.number_of_cards} />
            <DetailRow label="Rate" value={order.rate} />
            <DetailRow label="Owner staff ID" value={order.staff_id || 'Unclaimed'} />
            <DetailRow label="User remark" value={order.user_remark || 'N/A'} />
            <DetailRow label="Staff remark" value={order.staff_remark || 'N/A'} />
            <DetailRow label="Created at" value={fDateTime(order.created_at)} />
            <DetailRow label="Updated at" value={fDateTime(order.updated_at)} />
          </TableBody>
        </Table>
      </Card>
    );
  }, [details, order, tab]);

  if (!order) {
    return (
      <Paper
        elevation={0}
        sx={{
          height: 'calc(100vh - 180px)',
          minHeight: 640,
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          display: 'grid',
          placeItems: 'center',
          p: 4,
        }}
      >
        <Box textAlign="center">
          <Iconify icon="solar:ticket-bold" width={36} height={36} />
          <Typography variant="h6" sx={{ mt: 1.5, mb: 0.5 }}>
            Open a selected order
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select an order on the left, then open it to inspect details and actions.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Grid container spacing={2.5} sx={{ height: 'calc(100vh - 180px)', minHeight: 640 }}>
      <Grid item xs={12} xl={8}>
        <Paper
          elevation={0}
          sx={{
            height: '100%',
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            p: 2.25,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <Typography variant="h5">{order.order_id}</Typography>
                <AdminStatusChip status={order.status || 'pending'} />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <OwnershipNotice details={details} />
                {details?.is_elevated_admin && <Chip color="info" label="Elevated admin" size="small" />}
              </Stack>
            </Box>

            <OrderActionPanel
              actionLoading={actionLoading}
              canAct={canAct}
              canClaim={canClaim}
              details={details}
              onClaim={onClaim}
              onCloseOrder={onCloseOrder}
              onOpenComplete={onOpenComplete}
              onOpenDispute={onOpenDispute}
              onOpenReject={onOpenReject}
            />
          </Stack>

          {detailsLoading ? (
            <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
              <Stack spacing={2} alignItems="center">
                <CircularProgress size={34} />
                <Typography variant="body2" color="text.secondary">
                  Loading order details
                </Typography>
              </Stack>
            </Box>
          ) : (
            <>
              <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ mb: 2 }} variant="scrollable" allowScrollButtonsMobile>
                <Tab label="Details" value="details" />
                <Tab
                  value="images"
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>Images</span>
                      <Badge color="primary" badgeContent={imageCount} max={99} />
                    </Stack>
                  }
                />
                <Tab label="Audits" value="audits" />
              </Tabs>

              <Box sx={{ flex: 1, minHeight: 0 }}>{tabContent}</Box>
            </>
          )}
        </Paper>
      </Grid>

      {!detailsLoading && (
        <Grid item xs={12} xl={4}>
          <OrderEventTimeline events={details?.events || []} loading={false} dense title="Order timeline" />
        </Grid>
      )}
    </Grid>
  );
}

export default memo(CardRedemptionOrderDetailView);

CardRedemptionOrderDetailView.propTypes = {
  actionLoading: PropTypes.string,
  details: PropTypes.object,
  detailsLoading: PropTypes.bool.isRequired,
  onClaim: PropTypes.func.isRequired,
  onCloseOrder: PropTypes.func.isRequired,
  onOpenComplete: PropTypes.func.isRequired,
  onOpenDispute: PropTypes.func.isRequired,
  onOpenReject: PropTypes.func.isRequired,
  order: PropTypes.object,
};
